/**
 * Typed fetch wrappers for /api/billing/*. Mirrors the auth-api.ts pattern —
 * plain fetch with `credentials: 'include'` so the BA session cookie rides
 * along.
 *
 * `apiBaseUrl` comes from `useApiClient().getBaseURL()` (e.g. `/api` or
 * `https://app.flowlib.dev/api`). The billing route is mounted at
 * `${apiBaseUrl}/billing/*` directly on the worker.
 */

export type PlanId = 'free' | 'pro' | 'enterprise';
export type BillingCycle = 'monthly' | 'annual';
export type PlanStatus = string; // 'active' | 'past_due' | 'suspended' | …

export interface StripePriceInfo {
  cycle: BillingCycle;
  unitAmount: number;
  currency: string;
  priceId: string;
}

export interface PlanWithPrices {
  id: PlanId;
  name: string;
  monthlyCaps: { flowRuns: number };
  selfServe: boolean;
  prices: StripePriceInfo[];
}

export interface CurrentPlan {
  plan: PlanId;
  planName: string;
  caps: PlanWithPrices['monthlyCaps'];
  status: PlanStatus;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  cancelAt: number | null;
  billingCycle: BillingCycle | null;
}

export type Usage = Record<string, number>;

export interface BillingInvoiceSummary {
  id: string;
  status: string | null;
  amountDue: number | null;
  amountPaid: number | null;
  amountRemaining: number | null;
  currency: string | null;
  createdAt: number | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface BillingSummary {
  stripeConfigured: boolean;
  customerEmail: string | null;
  defaultPaymentMethod: {
    id: string;
    brand: string | null;
    last4: string | null;
  } | null;
  invoices: BillingInvoiceSummary[];
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${url} → ${res.status}: ${body || res.statusText}`);
  }
  return (await res.json()) as T;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${url} → ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
}

export function listPlans(apiBase: string): Promise<PlanWithPrices[]> {
  return getJson<PlanWithPrices[]>(`${apiBase}/billing/plans`);
}

export function getCurrentPlan(apiBase: string): Promise<CurrentPlan> {
  return getJson<CurrentPlan>(`${apiBase}/billing/plan`);
}

export function getUsage(apiBase: string): Promise<Usage> {
  return getJson<Usage>(`${apiBase}/billing/usage`);
}

export function getBillingSummary(apiBase: string): Promise<BillingSummary> {
  return getJson<BillingSummary>(`${apiBase}/billing/summary`);
}

export function startCheckout(
  apiBase: string,
  args: { planId: PlanId; cycle: BillingCycle; returnUrl?: string },
): Promise<{ url: string }> {
  return postJson<{ url: string }>(`${apiBase}/billing/checkout`, args);
}

export function openPortal(
  apiBase: string,
  args: { returnUrl?: string } = {},
): Promise<{ url: string }> {
  return postJson<{ url: string }>(`${apiBase}/billing/portal`, args);
}
